import { useState, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Label } from './label';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import type { ValidationRule } from '@/hooks/use-validation';
import { validators } from '@/hooks/use-validation';

// Re-export for convenience
export { ValidationRule, validators };

interface FormFieldProps {
  /** Field label text or element */
  label?: string | React.ReactNode;
  /** Optional helper text shown below the field */
  helperText?: string;
  /** Current error message (from validation) */
  error?: string | null;
  /** Whether the field has been touched (blurred) */
  touched?: boolean;
  /** Whether the field has been modified */
  dirty?: boolean;
  /** Whether to show the error state visually */
  showError?: boolean;
  /** Whether the field is valid (green check) */
  isValid?: boolean;
  /** Additional wrapper class name */
  className?: string;
  /** The input/select/textarea element */
  children?: React.ReactNode;
  /** Label element ID (for aria-describedby) */
  id?: string;
}

/**
 * FormField wraps an input with label, validation state, helper/error text.
 * Can be used standalone or with the useFormField hook.
 *
 * Usage:
 * ```tsx
 * <FormField label="Email" error={emailError} touched showError={!!emailError && touched}>
 *   <Input value={...} onChange={...} onBlur={...} />
 * </FormField>
 * ```
 */
export function FormField({
  label,
  helperText,
  error,
  touched,
  dirty,
  showError,
  isValid,
  className,
  children,
  id,
}: FormFieldProps) {
  const inputId = id || `field-${typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : Math.random().toString(36).slice(2, 9)}`;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;

  const showValidation = showError || (touched && !!error);
  const showSuccess = isValid && (touched || dirty);

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label htmlFor={inputId} className="flex items-center gap-1.5">
          {label}
          {showValidation && (
            <AlertCircle size={12} className="text-red-400 animate-in fade-in-0 zoom-in-0" />
          )}
          {showSuccess && !showValidation && (
            <CheckCircle2 size={12} className="text-dyndev-green animate-in fade-in-0 zoom-in-0" />
          )}
        </Label>
      )}

      {/* Children (the input element) */}
      <div className="relative">
        {children}
        {/* Validation indicator bar */}
        <div
          className={cn(
            'absolute bottom-0 left-2 right-2 h-[2px] rounded-full transition-all duration-300',
            showValidation && 'bg-red-400',
            showSuccess && !showValidation && 'bg-dyndev-green',
            !showValidation && !showSuccess && 'w-0 opacity-0',
          )}
        />
      </div>

      {/* Validation error text */}
      {showValidation && error && (
        <p
          id={errorId}
          role="alert"
          className="text-[11px] text-red-400 animate-in fade-in-0 slide-in-from-top-1 duration-200 flex items-center gap-1"
        >
          <AlertCircle size={10} />
          {error}
        </p>
      )}

      {/* Helper text */}
      {!showValidation && helperText && (
        <p id={helperId} className="text-[11px] text-[var(--text-secondary)]">
          {helperText}
        </p>
      )}
    </div>
  );
}

/**
 * Convenience hook for managing a single field's validation state.
 * Returns value, error state, and change/blur handlers to pass to FormField + Input.
 */
export function useFieldValidation(rules: ValidationRule[] = []) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [dirty, setDirty] = useState(false);

  const validate = useCallback(
    (val: string): string | null => {
      for (const rule of rules) {
        const err = rule(val);
        if (err) return err;
      }
      return null;
    },
    [rules],
  );

  const onChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      setDirty(true);
      const err = validate(newValue);
      setError(err);
    },
    [validate],
  );

  const onBlur = useCallback(() => {
    setTouched(true);
    if (!touched) {
      const err = validate(value);
      setError(err);
    }
  }, [validate, value, touched]);

  const reset = useCallback(() => {
    setValue('');
    setError(null);
    setTouched(false);
    setDirty(false);
  }, []);

  const isValid = useMemo(() => {
    const err = validate(value);
    return err === null && value.length > 0;
  }, [validate, value]);

  return {
    value,
    error,
    touched,
    dirty,
    isValid,
    showError: (touched || dirty) && error !== null,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    onBlur,
    reset,
    setValue: (v: string) => {
      setValue(v);
      setDirty(true);
      setError(validate(v));
    },
  };
}
